// Types specific to Automated Controls
// This file extends the base types with automated-specific fields

import { ControlOwner, RelatedAccount, OperatingEffectivenessStep, RollforwardInquiry, DependentControl } from "./types";

export interface GitcControl {
  id: string;
  controlId: string;
  controlName: string;
  typeOfControl: string;
  nature: string;
}

export interface ConfigurationSetting {
  id: string;
  name: string;
  description: string;
}

export interface Variant {
  id: string;
  name: string;
  description: string;
}

export interface SourceData {
  id: string;
  name: string;
  description: string;
  hasGitcs: boolean;
  gitcDetails: string;
  gitcControls: GitcControl[];
  gitcDocuments?: Array<{
    id: string;
    type: 'textbox' | 'linked' | 'uploaded';
    name: string;
    reference?: string;
    url?: string;
    content?: string;
    textResponse?: { html: string; plainText: string };
  }>;
  gitcTextResponse?: { html: string; plainText: string };
  hasBusinessControls: boolean;
  businessControlDetails: string;
  directControls: GitcControl[];
  directDocuments?: Array<{
    id: string;
    type: 'textbox' | 'linked' | 'uploaded';
    name: string;
    reference?: string;
    url?: string;
    content?: string;
    textResponse?: { html: string; plainText: string };
  }>;
  directTextResponse?: { html: string; plainText: string };
}

export interface AutomatedControlData {
  // Control Summary - Control Details
  controlId: string;
  controlName: string;
  workspaceLinked: string[];
  controlDescription: string;
  relatedAccounts?: RelatedAccount[];
  
  // Control Attributes
  typeOfControl: "Direct" | "Indirect" | "GITC";
  nature: "Automated";
  approach: "Preventive" | "Detective";
  controlType: "Automated Input Control" | "Automated processing control" | "Automated output control" | "Automated calculation control" | "Automated application access control" | "";
  
  // Tailoring Questions
  testOperatingEffectiveness: boolean;
  
  // Evaluate Design & Determine Implementation
  associatedItElements: string[]; // Placeholder for future IT element association
  
  // Design & Implementation
  gitcControlsOverAccess: GitcControl[];
  reliesOnSystemConfiguration: "Yes" | "No" | "";
  configurationSettings: ConfigurationSetting[];
  managementIntendedDesign: string;
  accessToModifyConfiguration: string;
  configurationChangeFrequency: string;
  proceduresToUnderstandDesign: string;
  gitcControlsOverSystemConfig: GitcControl[];
  hasVariations: "Yes" | "No" | "";
  proceduresToUnderstandVariants: string;
  additionalProcedures: string;
  variants: Variant[];
  
  // Source Data
  sourceData: SourceData[];
  
  // Design Factors
  designFactor1Response: string;
  designFactor2Enabled: boolean;
  controlOwners: ControlOwner[];
  designFactor3Enabled: boolean;
  frequency: string;
  consistency: string;
  designFactor4Enabled: boolean;
  designFactor4Content: string;
  designFactor5Enabled: boolean;
  designFactor5Content: string;
  
  // Dependent Controls
  isDependentOnOtherControls: boolean;
  dependentControls: DependentControl[];
  
  // Effectiveness of Design & Implementation
  isDesignEffective: "Yes" | "No" | "Not Yet Concluded";
  isProperlyImplemented: "Yes" | "No" | "Not Yet Concluded";
  
  // Risk Associated with Control (conditional on tailoring question)
  riskAssociated: "Higher" | "Not Higher";
  riskBasisConclusion: string;
  
  // Operating Effectiveness (conditional on tailoring question)
  timingOfProcedure: "Conclude at Interim"; // Always this for automated
  riskOfMaterialMisstatement: string;
  riskAssociatedWithControl: string;
  sampleSize: string;
  sampleSizeRationale: string;
  usingWorkOfOthers: "Yes" | "No" | "";
  workOfOthersApproach: "Selected For Reperformance" | "Not Selected For Reperformance" | "";
  isInformationUsed: "Yes" | "No" | "";
  operatingProcedures: OperatingEffectivenessStep[];
  isOperatingEffective: "Yes" | "No" | "Not Yet Concluded";
  
  // Rollforward
  rollforwardFromDate: string;
  rollforwardToDate: string;
  rollforwardInquiries: RollforwardInquiry[];
  hasIcfrChanges: boolean;
  icfrChanges: string;
  additionalProceduresRollforward: string;
  gitcOperatingEffectively: boolean;
  gitcDetails: string;
  isInquirySufficient: boolean;
  rollforwardSteps: OperatingEffectivenessStep[];
  rollforwardOperatingEffective: "Yes" | "No" | "Not Yet Concluded";
  rollforwardOperatingTestsDocuments: Array<{
    id: string;
    type: 'textbox' | 'linked' | 'uploaded';
    name: string;
    reference?: string;
    url?: string;
    content?: string;
    textResponse?: { html: string; plainText: string };
  }>;
  rollforwardOperatingTestsTextResponse?: { html: string; plainText: string };
}

export const AUTOMATED_CONTROL_TYPES = [
  "Automated Input Control",
  "Automated processing control",
  "Automated output control",
  "Automated calculation control",
  "Automated application access control",
] as const;

export const getDefaultAutomatedControlData = (): AutomatedControlData => ({
  controlId: "IC-001",
  controlName: "",
  workspaceLinked: [],
  controlDescription: "",
  typeOfControl: "Direct",
  nature: "Automated",
  approach: "Preventive",
  controlType: "",
  testOperatingEffectiveness: false,
  associatedItElements: [],
  gitcControlsOverAccess: [],
  reliesOnSystemConfiguration: "",
  configurationSettings: [],
  managementIntendedDesign: "",
  accessToModifyConfiguration: "",
  configurationChangeFrequency: "",
  proceduresToUnderstandDesign: "",
  gitcControlsOverSystemConfig: [],
  hasVariations: "",
  proceduresToUnderstandVariants: "",
  additionalProcedures: "",
  variants: [],
  sourceData: [],
  designFactor1Response: "",
  designFactor2Enabled: false,
  controlOwners: [],
  designFactor3Enabled: false,
  frequency: "",
  consistency: "",
  designFactor4Enabled: false,
  designFactor4Content: "",
  designFactor5Enabled: false,
  designFactor5Content: "",
  isDependentOnOtherControls: false,
  dependentControls: [],
  isDesignEffective: "Not Yet Concluded",
  isProperlyImplemented: "Not Yet Concluded",
  riskAssociated: "Not Higher",
  riskBasisConclusion: "",
  timingOfProcedure: "Conclude at Interim",
  riskOfMaterialMisstatement: "",
  riskAssociatedWithControl: "",
  sampleSize: "",
  sampleSizeRationale: "",
  usingWorkOfOthers: "",
  workOfOthersApproach: "",
  isInformationUsed: "",
  operatingProcedures: [],
  isOperatingEffective: "Not Yet Concluded",
  rollforwardFromDate: "",
  rollforwardToDate: "",
  rollforwardInquiries: [],
  hasIcfrChanges: false,
  icfrChanges: "",
  additionalProceduresRollforward: "",
  gitcOperatingEffectively: false,
  gitcDetails: "",
  isInquirySufficient: true,
  rollforwardSteps: [],
  rollforwardOperatingEffective: "Not Yet Concluded",
  rollforwardOperatingTestsDocuments: [],
  rollforwardOperatingTestsTextResponse: undefined,
  relatedAccounts: [],
});

